import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, FileText, Settings, Plus, LogOut, Menu, X, Trash2, Edit, Eye, Save,
    Search, Image, Upload, ExternalLink, Globe, CheckCircle, AlertCircle,
    ChevronRight, RefreshCw, Info, Phone, Mail, MapPin, Clock, Facebook, Instagram, Twitter,
    Sparkles, TrendingUp, Users, Wrench, Bell, Download
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useSettings } from '../context/SettingsContext';
import { API_URL, SITE_URL, getAuthHeader } from '../config/api';

// Input Field Component
const InputField = ({ label, name, value, onChange, type = 'text', placeholder = '', icon: Icon, helpText = '' }) => {
    const handleChange = useCallback((e) => {
        onChange(name, e.target.value);
    }, [name, onChange]);

    return (
        <div className="space-y-1.5">
            <label className="flex items-center text-sm font-semibold text-gray-700">
                {Icon && <Icon size={16} className="mr-2 text-blue-500" />}
                {label}
            </label>
            {type === 'textarea' ? (
                <textarea
                    name={name}
                    value={value || ''}
                    onChange={handleChange}
                    placeholder={placeholder}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                />
            ) : (
                <input
                    type={type}
                    name={name}
                    value={value || ''}
                    onChange={handleChange}
                    placeholder={placeholder}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
            )}
            {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
        </div>
    );
};

// Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder = '' }) => {
    const execCommand = (command, value = null) => {
        document.execCommand(command, false, value);
    };

    const handleBold = () => execCommand('bold');
    const handleItalic = () => execCommand('italic');
    const handleUnderline = () => execCommand('underline');
    const handleHeading = () => execCommand('formatBlock', 'H2');
    const handleParagraph = () => execCommand('formatBlock', 'P');
    const handleList = () => execCommand('insertUnorderedList');
    const handleOrderedList = () => execCommand('insertOrderedList');
    const handleLink = () => {
        const url = prompt('Link URL girin:');
        if (url) execCommand('createLink', false, url);
    };
    const handleQuote = () => execCommand('formatBlock', 'BLOCKQUOTE');
    const handleCode = () => execCommand('formatBlock', 'PRE');

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Toolbar */}
            <div className="bg-gray-50 border-b border-gray-200 p-2 flex flex-wrap gap-1">
                <button
                    type="button"
                    onClick={handleBold}
                    className="p-2 hover:bg-gray-200 rounded transition-colors font-bold"
                    title="Kalın (Ctrl+B)"
                >
                    B
                </button>
                <button
                    type="button"
                    onClick={handleItalic}
                    className="p-2 hover:bg-gray-200 rounded transition-colors italic"
                    title="İtalik (Ctrl+I)"
                >
                    I
                </button>
                <button
                    type="button"
                    onClick={handleUnderline}
                    className="p-2 hover:bg-gray-200 rounded transition-colors underline"
                    title="Altı Çizili (Ctrl+U)"
                >
                    U
                </button>
                <div className="w-px h-6 bg-gray-300 mx-1"></div>
                <button
                    type="button"
                    onClick={handleHeading}
                    className="p-2 hover:bg-gray-200 rounded transition-colors text-sm font-semibold"
                    title="Başlık (H2)"
                >
                    H2
                </button>
                <button
                    type="button"
                    onClick={handleParagraph}
                    className="p-2 hover:bg-gray-200 rounded transition-colors text-sm"
                    title="Paragraf"
                >
                    P
                </button>
                <div className="w-px h-6 bg-gray-300 mx-1"></div>
                <button
                    type="button"
                    onClick={handleList}
                    className="p-2 hover:bg-gray-200 rounded transition-colors text-sm"
                    title="Madde İşaretli Liste"
                >
                    • Liste
                </button>
                <button
                    type="button"
                    onClick={handleOrderedList}
                    className="p-2 hover:bg-gray-200 rounded transition-colors text-sm"
                    title="Numaralı Liste"
                >
                    1. Liste
                </button>
                <div className="w-px h-6 bg-gray-300 mx-1"></div>
                <button
                    type="button"
                    onClick={handleQuote}
                    className="p-2 hover:bg-gray-200 rounded transition-colors text-sm"
                    title="Alıntı"
                >
                    ❝ Alıntı
                </button>
                <button
                    type="button"
                    onClick={handleCode}
                    className="p-2 hover:bg-gray-200 rounded transition-colors text-sm font-mono"
                    title="Kod Bloğu"
                >
                    {'</>'}
                </button>
                <div className="w-px h-6 bg-gray-300 mx-1"></div>
                <button
                    type="button"
                    onClick={handleLink}
                    className="p-2 hover:bg-gray-200 rounded transition-colors text-sm"
                    title="Link Ekle"
                >
                    🔗 Link
                </button>
            </div>
            
            {/* Editor */}
            <textarea
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={15}
                className="w-full px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none font-mono text-sm whitespace-pre-wrap"
                style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
            />
            
            {/* Help text */}
            <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-xs text-gray-500 space-y-1">
                <p>💡 <strong>İpucu:</strong> HTML etiketleri kullanabilirsiniz:</p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded block mt-1">
                    &lt;p&gt;Paragraf&lt;/p&gt; • &lt;strong&gt;Kalın&lt;/strong&gt; • &lt;ul&gt;&lt;li&gt;Liste&lt;/li&gt;&lt;/ul&gt; • &lt;h2&gt;Başlık&lt;/h2&gt;
                </code>
                <p className="mt-2">📋 Word veya başka bir yerden kopyalayıp yapıştırabilirsiniz - format korunur!</p>
            </div>
        </div>
    );
};

// Section Card Component
const SectionCard = ({ title, icon: Icon, children, actions }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
            <h3 className="flex items-center text-lg font-bold text-gray-800">
                {Icon && <Icon size={20} className="mr-3 text-blue-600" />}
                {title}
            </h3>
            {actions && <div className="flex items-center space-x-2">{actions}</div>}
        </div>
        <div className="p-6 space-y-6">
            {children}
        </div>
    </div>
);

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color = 'blue', trend }) => {
    const colorClasses = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-green-50 text-green-600',
        yellow: 'bg-yellow-50 text-yellow-600',
        purple: 'bg-purple-50 text-purple-600',
        red: 'bg-red-50 text-red-600',
    };

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500">{title}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
                    {trend && (
                        <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% geçen aya göre
                        </p>
                    )}
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
                    <Icon size={24} />
                </div>
            </div>
        </div>
    );
};

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { refreshSettings } = useSettings();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [posts, setPosts] = useState([]);
    const [settings, setSettings] = useState({});
    const [originalSettings, setOriginalSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [saveStatus, setSaveStatus] = useState('');
    const [error, setError] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [lastSync, setLastSync] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);

    // Gallery state
    const [galleryImages, setGalleryImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [galleryLoading, setGalleryLoading] = useState(false);

    // Environment info
    const isLocalDev = window.location.hostname === 'localhost';
    const USE_MOCK_DATA = isLocalDev; // Auto-detect: mock for local, real API for production

    const mockPosts = [
        { id: 1, title: 'Test Yazı', slug: 'test-yazi', content: 'Test içerik', excerpt: 'Test özet', status: 'published', created_at: '2024-01-01' },
        { id: 2, title: 'Taslak Yazı', slug: 'taslak-yazi', content: 'Taslak içerik', excerpt: 'Taslak özet', status: 'draft', created_at: '2024-01-02' }
    ];

    const mockSettings = {
        siteName: 'KIRIKKALE FAR VE STOP TAMİR MERKEZİ',
        phone: '05312127605',
        email: 'kirikkalefar@gmail.com',
        address: 'Sanayi, Karadeniz Cd. 48d, 71400 KIRIKKALE / Merkez',
        heroTitle: 'KIRIKKALE FAR | Far, Stop Tamir Merkezi',
        aboutTitle: 'KIRIKKALE FAR VE STOP TAMİR MERKEZİ',
        ctaTitle: 'İletişim'
    };

    const mockGallery = [
        { id: '1', filename: 'test1.jpg', url: '/gallery/test1.jpg' },
        { id: '2', filename: 'test2.jpg', url: '/gallery/test2.jpg' }
    ];

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setIsSyncing(true);
            setError('');

            // Use mock data for local development
            if (USE_MOCK_DATA) {
                console.log('📝 Using mock data for local development');
                await new Promise(resolve => setTimeout(resolve, 300)); // Simulate network delay
                setPosts(mockPosts);
                setSettings(mockSettings);
                setOriginalSettings(mockSettings);
                setGalleryImages(mockGallery);
                setHasChanges(false);
                setLastSync(new Date());
                setLoading(false);
                setIsSyncing(false);
                return;
            }

            // Use real API only - with error handling
            const [postsRes, settingsRes, galleryRes] = await Promise.all([
                fetch(`${API_URL}/admin`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/settings?_=${Date.now()}`),
                // Gallery API disabled - use local images
                Promise.resolve({ ok: false })
            ]);

            if (postsRes.status === 401 || postsRes.status === 403) {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUsername');
                navigate('/admin/login');
                return;
            }

            // Check if response is HTML (error page)
            const postsContentType = postsRes.headers.get('content-type');
            if (postsContentType && postsContentType.includes('text/html')) {
                throw new Error('Server returned HTML instead of JSON (API error)');
            }

            const postsData = await postsRes.json();
            const settingsData = await settingsRes.json();
            // Gallery uses local images
            const galleryData = [];

            setPosts(Array.isArray(postsData) ? postsData : []);
            setSettings(settingsData);
            setOriginalSettings(settingsData);
            setGalleryImages([]);
            setHasChanges(false);
            setLastSync(new Date());
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Veritabanına bağlanılamadı: ' + err.message);
        } finally {
            setLoading(false);
            setIsSyncing(false);
        }
    }, [getAuthHeader, navigate]);

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
            return;
        }
        fetchData();
    }, [navigate, fetchData]);

    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            if (!hasChanges) {
                fetchData();
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [fetchData, hasChanges]);

    const fetchGallery = useCallback(async () => {
        setGalleryLoading(true);
        try {
            // Fetch from API - will include ALL images in gallery folder
            const response = await fetch(`${API_URL}/gallery?_=${Date.now()}`);
            
            if (!response.ok) {
                throw new Error('Gallery API error: ' + response.status);
            }

            const data = await response.json();
            // Add all images from API response
            const images = Array.isArray(data) ? data : [];
            setGalleryImages(images);
            
            console.log('✅ Gallery loaded:', images.length, 'images');
        } catch (err) {
            // Fallback to local gallery images if API fails
            console.warn('⚠️ Using local gallery fallback:', err.message);
            const localImages = [
                { id: '1', filename: '2046030901.jpg', url: '/gallery/2046030901.jpg', path: '/gallery/2046030901.jpg' },
                { id: '2', filename: '2050424694.jpg', url: '/gallery/2050424694.jpg', path: '/gallery/2050424694.jpg' },
                { id: '3', filename: '2105620173.jpg', url: '/gallery/2105620173.jpg', path: '/gallery/2105620173.jpg' },
                { id: '4', filename: '2107131574.jpg', url: '/gallery/2107131574.jpg', path: '/gallery/2107131574.jpg' },
                { id: '5', filename: '2170730226.jpg', url: '/gallery/2170730226.jpg', path: '/gallery/2170730226.jpg' },
                { id: '6', filename: '2174931569.jpg', url: '/gallery/2174931569.jpg', path: '/gallery/2174931569.jpg' },
                { id: '7', filename: '2219928384.jpg', url: '/gallery/2219928384.jpg', path: '/gallery/2219928384.jpg' },
                { id: '8', filename: '2244530033.jpg', url: '/gallery/2244530033.jpg', path: '/gallery/2244530033.jpg' },
                { id: '9', filename: '2280126468.jpg', url: '/gallery/2280126468.jpg', path: '/gallery/2280126468.jpg' },
                { id: '10', filename: '2293029886.jpg', url: '/gallery/2293029886.jpg', path: '/gallery/2293029886.jpg' },
                { id: '11', filename: '2351729842.jpg', url: '/gallery/2351729842.jpg', path: '/gallery/2351729842.jpg' },
                { id: '12', filename: '2353329841.jpg', url: '/gallery/2353329841.jpg', path: '/gallery/2353329841.jpg' },
                { id: '13', filename: '2371220039.jpg', url: '/gallery/2371220039.jpg', path: '/gallery/2371220039.jpg' },
                { id: '14', filename: '2373424190.jpg', url: '/gallery/2373424190.jpg', path: '/gallery/2373424190.jpg' },
                { id: '15', filename: '2459730470.jpg', url: '/gallery/2459730470.jpg', path: '/gallery/2459730470.jpg' },
                { id: '16', filename: '2470228331.jpg', url: '/gallery/2470228331.jpg', path: '/gallery/2470228331.jpg' },
                { id: '17', filename: '2506631841.jpg', url: '/gallery/2506631841.jpg', path: '/gallery/2506631841.jpg' },
                { id: '18', filename: '2607630724.jpg', url: '/gallery/2607630724.jpg', path: '/gallery/2607630724.jpg' },
                { id: '19', filename: '2632328398.jpg', url: '/gallery/2632328398.jpg', path: '/gallery/2632328398.jpg' },
                { id: '20', filename: '2725721399.jpg', url: '/gallery/2725721399.jpg', path: '/gallery/2725721399.jpg' },
                { id: '21', filename: '2745631858.jpg', url: '/gallery/2745631858.jpg', path: '/gallery/2745631858.jpg' },
                { id: '22', filename: '2759522331.jpg', url: '/gallery/2759522331.jpg', path: '/gallery/2759522331.jpg' },
                { id: '23', filename: '2816027175.jpg', url: '/gallery/2816027175.jpg', path: '/gallery/2816027175.jpg' },
                { id: '24', filename: '2841523640.jpg', url: '/gallery/2841523640.jpg', path: '/gallery/2841523640.jpg' },
                { id: '25', filename: '2848629464.jpg', url: '/gallery/2848629464.jpg', path: '/gallery/2848629464.jpg' },
                { id: '26', filename: '2874921060.jpg', url: '/gallery/2874921060.jpg', path: '/gallery/2874921060.jpg' },
                { id: '27', filename: '2933020471.jpg', url: '/gallery/2933020471.jpg', path: '/gallery/2933020471.jpg' },
                { id: '28', filename: '2940327377.jpg', url: '/gallery/2940327377.jpg', path: '/gallery/2940327377.jpg' },
                { id: '29', filename: '3064228505.jpg', url: '/gallery/3064228505.jpg', path: '/gallery/3064228505.jpg' },
                { id: '30', filename: '3111724699.jpg', url: '/gallery/3111724699.jpg', path: '/gallery/3111724699.jpg' },
            ];
            setGalleryImages(localImages);
        } finally {
            setGalleryLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'gallery') {
            fetchGallery();
        }
    }, [activeTab, fetchGallery]);

    const handleLogout = useCallback(() => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUsername');
        navigate('/admin/login');
    }, [navigate]);

    const handleSettingChange = useCallback((key, value) => {
        setSettings(prev => {
            const newSettings = { ...prev, [key]: value };
            setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(originalSettings));
            return newSettings;
        });
    }, [originalSettings]);

    const saveSettings = useCallback(async () => {
        try {
            setSaveStatus('Kaydediliyor...');

            // Mock save for local development
            if (USE_MOCK_DATA) {
                await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
                console.log('💾 Mock save - settings saved locally');
                setOriginalSettings(settings);
                setHasChanges(false);
                setLastSync(new Date());
                setSaveStatus('✓ Kaydedildi (Mock)');
                
                toast({
                    title: "✅ Başarılı! (Mock)",
                    description: "Ayarlar yerel olarak kaydedildi. Production API kullanılmıyor.",
                    variant: "default",
                });
                setTimeout(() => setSaveStatus(''), 3000);
                return;
            }

            // Use real API only
            const token = localStorage.getItem('adminToken');
            const headers = {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/settings`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(settings)
            });

            // Check if response is HTML
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('text/html')) {
                throw new Error('Server hatası: HTML yanıtı alındı');
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Kaydetme hatası');
            }

            setOriginalSettings(settings);
            setHasChanges(false);
            await refreshSettings();
            setLastSync(new Date());

            toast({
                title: "✅ Başarılı!",
                description: "Ayarlar kaydedildi ve siteye anında yansıdı.",
                variant: "default",
            });
            setSaveStatus('✓ Kaydedildi');
            setTimeout(() => setSaveStatus(''), 3000);
        } catch (err) {
            console.error('Save error:', err);
            setError('Ayarlar kaydedilemedi: ' + err.message);
            setSaveStatus('❌ Hata');
            toast({
                title: "Hata!",
                description: err.message || "Ayarlar kaydedilirken bir hata oluştu.",
                variant: "destructive",
            });
        }
    }, [settings, refreshSettings, getAuthHeader]);

    const resetChanges = useCallback(() => {
        setSettings(originalSettings);
        setHasChanges(false);
        toast({
            title: "Değişiklikler iptal edildi",
            description: "Önceki ayarlara geri dönüldü.",
        });
    }, [originalSettings]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm('Bu yazıyı silmek istediğinizden emin misiniz?')) return;

        try {
            const response = await fetch(`${API_URL}/admin/${id}`, {
                method: 'DELETE',
                headers: getAuthHeader()
            });
            if (!response.ok) throw new Error('Silme hatası');

            setPosts(prev => prev.filter(p => p.id !== id));
            toast({
                title: "✅ Başarılı!",
                description: "Yazı başarıyla silindi.",
                variant: "default",
            });
        } catch (err) {
            setError('Yazı silinemedi: ' + err.message);
            toast({
                title: "Hata!",
                description: "Yazı silinirken bir hata oluştu.",
                variant: "destructive",
            });
        }
    }, [getAuthHeader]);

    const handleEdit = useCallback((post) => {
        setEditingPost(post);
        setFormData({
            title: post.title,
            slug: post.slug,
            content: post.content || '',
            excerpt: post.excerpt || '',
            image: post.image || '',
            status: post.status,
            meta_description: post.meta_description || '',
            meta_keywords: post.meta_keywords || ''
        });
        setShowModal(true);
    }, []);

    const resetForm = useCallback(() => {
        setEditingPost(null);
        setFormData({
            title: '',
            slug: '',
            content: '',
            excerpt: '',
            image: '',
            status: 'draft',
            meta_description: '',
            meta_keywords: ''
        });
    }, []);

    const openNewPostModal = useCallback(() => {
        resetForm();
        setShowModal(true);
    }, [resetForm]);

    const generateSlug = useCallback((title) => {
        return title.toLowerCase()
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ı/g, 'i')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    }, []);

    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        image: '',
        status: 'draft',
        meta_description: '',
        meta_keywords: ''
    });

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        try {
            // Use real API only
            const url = editingPost
                ? `${API_URL}/admin/${editingPost.id}`
                : `${API_URL}/admin`;

            const response = await fetch(url, {
                method: editingPost ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    ...getAuthHeader()
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error('Kaydetme hatası');

            fetchData();
            setShowModal(false);
            resetForm();
            toast({
                title: "✅ Başarılı!",
                description: editingPost ? "Yazı başarıyla güncellendi." : "Yazı başarıyla oluşturuldu.",
                variant: "default",
            });
        } catch (err) {
            setError('Yazı kaydedilemedi: ' + err.message);
            toast({
                title: "Hata!",
                description: "Yazı kaydedilirken bir hata oluştu.",
                variant: "destructive",
            });
        }
    }, [editingPost, formData, fetchData, resetForm, getAuthHeader]);

    const handleImageUpload = useCallback(async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        console.log('📷 Uploading image:', file.name, 'Size:', file.size, 'Type:', file.type);

        const token = localStorage.getItem('adminToken');
        if (!token) {
            toast({
                title: "Hata!",
                description: "Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.",
                variant: "destructive",
            });
            return;
        }

        setUploading(true);
        try {
            // Use real API for upload
            const formData = new FormData();
            formData.append('image', file);

            console.log('📤 Upload request to:', `${API_URL}/gallery`);
            
            const response = await fetch(`${API_URL}/gallery`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            console.log('📥 Upload response status:', response.status);
            console.log('📥 Response headers:', response.headers);
            
            // Get response text first to see what we got
            const responseText = await response.text();
            console.log('📥 Response text:', responseText.substring(0, 500));
            
            // Check if response is HTML
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('text/html')) {
                throw new Error('Server HTML hatası döndürdü. Gallery klasör izinlerini kontrol edin (755) ve .htaccess dosyasının doğru olduğundan emin olun.');
            }
            
            // Try to parse as JSON
            let responseData;
            try {
                responseData = JSON.parse(responseText);
            } catch (parseErr) {
                throw new Error('Geçersiz yanıt: JSON değil. Server hatası: ' + responseText.substring(0, 200));
            }
            
            console.log('📥 Upload response:', responseData);

            if (!response.ok) {
                throw new Error(responseData.error || 'Yükleme hatası: ' + response.status);
            }

            toast({
                title: "✅ Başarılı!",
                description: "Resim başarıyla yüklendi.",
                variant: "default",
            });
            
            // Refresh gallery to show new image
            await fetchGallery();
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.message || 'Resim yüklenemedi');
            toast({
                title: "Hata!",
                description: err.message || "Resim yüklenirken bir hata oluştu.",
                variant: "destructive",
            });
        } finally {
            setUploading(false);
        }
    }, [fetchGallery]);

    const handleDeleteImage = useCallback(async (filename) => {
        if (!window.confirm('Bu resmi silmek istediğinizden emin misiniz?')) return;

        try {
            const response = await fetch(`${API_URL}/gallery/${filename}`, {
                method: 'DELETE',
                headers: getAuthHeader()
            });
            if (!response.ok) throw new Error('Delete error');

            setGalleryImages(prev => prev.filter(img => img.filename !== filename));
            toast({
                title: "✅ Başarılı!",
                description: "Resim başarıyla silindi.",
                variant: "default",
            });
        } catch (err) {
            setError('Resim silinemedi: ' + err.message);
            toast({
                title: "Hata!",
                description: "Resim silinirken bir hata oluştu.",
                variant: "destructive",
            });
        }
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('tr-TR');
    }, []);

    // Filter posts based on search and status
    const filteredPosts = posts.filter(post => {
        const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.slug.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || post.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600">Yükleniyor...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
            {/* Sidebar */}
            <aside className={`${sidebarOpen ? 'w-72' : 'w-20'} bg-white shadow-xl transition-all duration-300 flex flex-col`}>
                {/* Logo */}
                <div className="p-6 flex items-center justify-between border-b border-gray-100">
                    {sidebarOpen ? (
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                            <p className="text-xs text-gray-500">KIRIKKALE FAR</p>
                        </div>
                    ) : (
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">K</div>
                    )}
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                {/* Environment Badge */}
                {sidebarOpen && isLocalDev && (
                    <div className="px-4 py-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            🛠️ Local Development
                        </span>
                    </div>
                )}

                {/* View Site Button */}
                <div className="p-4">
                    <a
                        href={SITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-200"
                    >
                        <Globe size={18} />
                        {sidebarOpen && <span className="ml-2 font-medium">Siteyi Görüntüle</span>}
                    </a>
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-3 py-4 space-y-1">
                    {[
                        { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                        { id: 'posts', icon: FileText, label: 'Blog Yazıları', badge: posts.filter(p => p.status === 'draft').length },
                        { id: 'content', icon: Settings, label: 'Site İçerikleri' },
                        { id: 'contact', icon: Phone, label: 'İletişim Bilgileri' },
                        { id: 'gallery', icon: Image, label: 'Galeri', badge: galleryImages.length },
                    ].map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`w-full flex items-center px-4 py-3 rounded-xl transition-all ${activeTab === item.id
                                ? 'bg-blue-50 text-blue-600 font-semibold'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            <item.icon size={20} />
                            {sidebarOpen && (
                                <>
                                    <span className="ml-3 flex-1 text-left">{item.label}</span>
                                    {item.badge > 0 && (
                                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                            {item.badge}
                                        </span>
                                    )}
                                </>
                            )}
                        </button>
                    ))}
                </nav>

                {/* Sync Status */}
                {sidebarOpen && lastSync && (
                    <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                        <div className="flex items-center">
                            <RefreshCw size={12} className={`mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                            Son senkronizasyon: {lastSync.toLocaleTimeString('tr-TR')}
                        </div>
                    </div>
                )}

                {/* Logout */}
                <div className="p-4 border-t border-gray-100">
                    <button onClick={handleLogout} className="w-full flex items-center px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors">
                        <LogOut size={20} />
                        {sidebarOpen && <span className="ml-3 font-medium">Çıkış Yap</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-8 overflow-auto">
                {/* Header */}
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            {activeTab === 'dashboard' && 'Dashboard'}
                            {activeTab === 'posts' && 'Blog Yazıları'}
                            {activeTab === 'content' && 'Site İçerikleri'}
                            {activeTab === 'contact' && 'İletişim Bilgileri'}
                            {activeTab === 'gallery' && 'Galeri Yönetimi'}
                        </h1>
                        <p className="text-gray-500 mt-1">
                            {activeTab === 'dashboard' && 'Site genel bakış ve istatistikler'}
                            {activeTab === 'posts' && 'Blog yazılarınızı yönetin'}
                            {activeTab === 'content' && 'Site içeriklerini düzenleyin'}
                            {activeTab === 'contact' && 'İletişim bilgilerinizi güncelleyin'}
                            {activeTab === 'gallery' && 'Galeri resimlerini yönetin'}
                        </p>
                    </div>
                    <div className="flex items-center space-x-3">
                        {hasChanges && (
                            <span className="text-orange-600 flex items-center bg-orange-50 px-4 py-2 rounded-lg">
                                <AlertCircle size={18} className="mr-2" />
                                Kaydedilmemiş değişiklikler var
                            </span>
                        )}
                        <button
                            onClick={fetchData}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Yenile"
                        >
                            <RefreshCw size={20} className={isSyncing ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center">
                        <AlertCircle size={20} className="mr-3" />
                        {error}
                        <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
                            <X size={20} />
                        </button>
                    </div>
                )}

                {/* Dashboard */}
                {activeTab === 'dashboard' && (
                    <div className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <StatCard
                                title="Toplam Yazı"
                                value={posts.length}
                                icon={FileText}
                                color="blue"
                            />
                            <StatCard
                                title="Yayında"
                                value={posts.filter(p => p.status === 'published').length}
                                icon={CheckCircle}
                                color="green"
                            />
                            <StatCard
                                title="Taslak"
                                value={posts.filter(p => p.status === 'draft').length}
                                icon={Clock}
                                color="yellow"
                            />
                            <StatCard
                                title="Galeri Resimleri"
                                value={galleryImages.length}
                                icon={Image}
                                color="purple"
                            />
                        </div>

                        {/* Quick Actions */}
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Hızlı İşlemler</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <button onClick={() => setActiveTab('posts')} className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all text-left group">
                                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                                        <Plus className="text-blue-600" size={24} />
                                    </div>
                                    <h3 className="font-semibold text-gray-900">Yeni Yazı Ekle</h3>
                                    <p className="text-sm text-gray-500 mt-1">Blog yazısı oluşturun</p>
                                </button>
                                <button onClick={() => setActiveTab('content')} className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all text-left group">
                                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                                        <Settings className="text-green-600" size={24} />
                                    </div>
                                    <h3 className="font-semibold text-gray-900">Site İçerikleri</h3>
                                    <p className="text-sm text-gray-500 mt-1">İçerikleri düzenleyin</p>
                                </button>
                                <button onClick={() => setActiveTab('gallery')} className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all text-left group">
                                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                                        <Upload className="text-purple-600" size={24} />
                                    </div>
                                    <h3 className="font-semibold text-gray-900">Resim Yükle</h3>
                                    <p className="text-sm text-gray-500 mt-1">Galeriye resim ekleyin</p>
                                </button>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Son Blog Yazıları</h2>
                            {posts.length > 0 ? (
                                <div className="space-y-3">
                                    {posts.slice(0, 5).map(post => (
                                        <div key={post.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                            <div className="flex items-center">
                                                <div className={`w-2 h-2 rounded-full mr-3 ${post.status === 'published' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                                                <div>
                                                    <p className="font-medium text-gray-900">{post.title}</p>
                                                    <p className="text-sm text-gray-500">{formatDate(post.created_at)}</p>
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-xs ${post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                {post.status === 'published' ? 'Yayında' : 'Taslak'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-center py-8">Henüz blog yazısı yok.</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Posts Tab */}
                {activeTab === 'posts' && (
                    <>
                        {/* Filters */}
                        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-4">
                            <div className="flex items-center space-x-4 w-full md:w-auto">
                                <div className="relative flex-1 md:w-64">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                    <input
                                        type="text"
                                        placeholder="Yazı ara..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    />
                                </div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                >
                                    <option value="all">Tümü</option>
                                    <option value="published">Yayında</option>
                                    <option value="draft">Taslak</option>
                                </select>
                            </div>
                            <button onClick={openNewPostModal} className="flex items-center bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 w-full md:w-auto justify-center">
                                <Plus size={20} className="mr-2" />
                                Yeni Yazı Ekle
                            </button>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Başlık</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Slug</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Durum</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Tarih</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredPosts.map((post) => (
                                        <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 font-medium text-gray-900">{post.title}</td>
                                            <td className="px-6 py-4 text-sm text-gray-500">{post.slug}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                    {post.status === 'published' ? 'Yayında' : 'Taslak'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">{formatDate(post.created_at)}</td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-blue-600 transition-colors" title="Görüntüle">
                                                        <Eye size={18} />
                                                    </a>
                                                    <button onClick={() => handleEdit(post)} className="p-2 text-gray-400 hover:text-blue-600 transition-colors" title="Düzenle">
                                                        <Edit size={18} />
                                                    </button>
                                                    <button onClick={() => handleDelete(post.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Sil">
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredPosts.length === 0 && (
                                <div className="text-center py-12 text-gray-500">
                                    <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                                    <p>Sonuç bulunamadı.</p>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Content Tab */}
                {activeTab === 'content' && (
                    <>
                        {/* Action Bar */}
                        <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                            <div className="flex items-center space-x-4">
                                <span className="text-sm text-gray-500">
                                    {hasChanges ? (
                                        <span className="flex items-center text-orange-600">
                                            <AlertCircle size={16} className="mr-1" />
                                            Kaydedilmemiş değişiklikler var
                                        </span>
                                    ) : (
                                        <span className="flex items-center text-green-600">
                                            <CheckCircle size={16} className="mr-1" />
                                            Tüm değişiklikler kaydedildi
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-center space-x-3">
                                {hasChanges && (
                                    <button onClick={resetChanges} className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors">
                                        İptal
                                    </button>
                                )}
                                <button
                                    onClick={saveSettings}
                                    disabled={!hasChanges}
                                    className={`flex items-center px-6 py-2 rounded-xl transition-all ${hasChanges
                                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-200'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    <Save size={18} className="mr-2" />
                                    {saveStatus || 'Kaydet'}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {/* Site Info */}
                            <SectionCard title="Site Bilgileri" icon={Info}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <InputField
                                        label="Site Adı"
                                        name="siteName"
                                        value={settings.siteName}
                                        onChange={handleSettingChange}
                                        placeholder="KIRIKKALE FAR VE STOP TAMİR MERKEZİ"
                                    />
                                    <InputField
                                        label="Site Açıklaması"
                                        name="siteDescription"
                                        value={settings.siteDescription}
                                        onChange={handleSettingChange}
                                        type="textarea"
                                        placeholder="İçi kararan, güneşten solan ve üzeri çizilmiş otomobil, kamyonet, otobüs ve motorsiklet FAR & STOP'larınızı yeni gibi yapıyoruz."
                                        helpText="Footer ve SEO'da kullanılır"
                                    />
                                </div>
                            </SectionCard>

                            {/* Hero Section */}
                            <SectionCard title="Ana Sayfa Hero Bölümü" icon={LayoutDashboard}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <InputField
                                        label="Hero Başlığı"
                                        name="heroTitle"
                                        value={settings.heroTitle}
                                        onChange={handleSettingChange}
                                        placeholder="KIRIKKALE FAR | Far, Stop Tamir Merkezi"
                                    />
                                    <InputField
                                        label="Hero Resim URL"
                                        name="heroImage"
                                        value={settings.heroImage}
                                        onChange={handleSettingChange}
                                        placeholder="/logo.jpg"
                                    />
                                    <div className="md:col-span-2">
                                        <InputField
                                            label="Hero Alt Başlığı"
                                            name="heroSubtitle"
                                            value={settings.heroSubtitle}
                                            onChange={handleSettingChange}
                                            type="textarea"
                                            placeholder="İçi kararan, güneşten solan ve üzeri çizilmiş otomobil, kamyonet, otobüs ve motorsiklet FAR & STOP'larınızı yeni gibi yapıyoruz."
                                        />
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Stats */}
                            <SectionCard title="İstatistikler (Hakkımızda)" icon={TrendingUp}>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <InputField
                                        label="Yıllık Deneyim"
                                        name="experienceYears"
                                        value={settings.experienceYears}
                                        onChange={handleSettingChange}
                                        placeholder="4+"
                                    />
                                    <InputField
                                        label="Mutlu Müşteri"
                                        name="happyCustomers"
                                        value={settings.happyCustomers}
                                        onChange={handleSettingChange}
                                        placeholder="1000+"
                                    />
                                    <InputField
                                        label="Müşteri Memnuniyeti"
                                        name="satisfaction"
                                        value={settings.satisfaction}
                                        onChange={handleSettingChange}
                                        placeholder="100%"
                                    />
                                    <InputField
                                        label="Uzman Hizmet"
                                        name="servicesCount"
                                        value={settings.servicesCount}
                                        onChange={handleSettingChange}
                                        placeholder="7"
                                    />
                                </div>
                            </SectionCard>
                        </div>
                    </>
                )}

                {/* Contact Tab */}
                {activeTab === 'contact' && (
                    <>
                        {/* Action Bar */}
                        <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                            <div className="flex items-center space-x-4">
                                <span className="text-sm text-gray-500">
                                    {hasChanges ? (
                                        <span className="flex items-center text-orange-600">
                                            <AlertCircle size={16} className="mr-1" />
                                            Kaydedilmemiş değişiklikler var
                                        </span>
                                    ) : (
                                        <span className="flex items-center text-green-600">
                                            <CheckCircle size={16} className="mr-1" />
                                            Tüm değişiklikler kaydedildi
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-center space-x-3">
                                {hasChanges && (
                                    <button onClick={resetChanges} className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors">
                                        İptal
                                    </button>
                                )}
                                <button
                                    onClick={saveSettings}
                                    disabled={!hasChanges}
                                    className={`flex items-center px-6 py-2 rounded-xl transition-all ${hasChanges
                                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-200'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    <Save size={18} className="mr-2" />
                                    {saveStatus || 'Kaydet'}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {/* Contact Info */}
                            <SectionCard title="İletişim Bilgileri" icon={Phone}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <InputField
                                        label="Telefon 1"
                                        name="phone"
                                        value={settings.phone}
                                        onChange={handleSettingChange}
                                        icon={Phone}
                                        placeholder="05312127605"
                                    />
                                    <InputField
                                        label="Telefon 2"
                                        name="phone2"
                                        value={settings.phone2}
                                        onChange={handleSettingChange}
                                        icon={Phone}
                                        placeholder=""
                                    />
                                    <InputField
                                        label="E-posta"
                                        name="email"
                                        value={settings.email}
                                        onChange={handleSettingChange}
                                        icon={Mail}
                                        type="email"
                                        placeholder="kirikkalefar@gmail.com"
                                    />
                                    <InputField
                                        label="Çalışma Saatleri"
                                        name="workingHours"
                                        value={settings.workingHours}
                                        onChange={handleSettingChange}
                                        icon={Clock}
                                        placeholder="Pazartesi - Cumartesi: 08:30 - 18:30"
                                    />
                                    <div className="md:col-span-2">
                                        <InputField
                                            label="Adres"
                                            name="address"
                                            value={settings.address}
                                            onChange={handleSettingChange}
                                            icon={MapPin}
                                            placeholder="Sanayi, Karadeniz Cd. 48d, 71400 KIRIKKALE / Merkez"
                                        />
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Social Media */}
                            <SectionCard title="Sosyal Medya" icon={Facebook}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <InputField
                                        label="Facebook"
                                        name="facebook"
                                        value={settings.facebook}
                                        onChange={handleSettingChange}
                                        icon={Facebook}
                                        placeholder="https://facebook.com/..."
                                    />
                                    <InputField
                                        label="Instagram"
                                        name="instagram"
                                        value={settings.instagram}
                                        onChange={handleSettingChange}
                                        icon={Instagram}
                                        placeholder="https://instagram.com/..."
                                    />
                                    <InputField
                                        label="Twitter/X"
                                        name="twitter"
                                        value={settings.twitter}
                                        onChange={handleSettingChange}
                                        icon={Twitter}
                                        placeholder="https://twitter.com/..."
                                    />
                                </div>
                            </SectionCard>
                        </div>
                    </>
                )}

                {/* Gallery Tab */}
                {activeTab === 'gallery' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Galeri Yönetimi</h2>
                                    <p className="text-sm text-gray-500 mt-1">{galleryImages.length} resim yüklü</p>
                                </div>
                                <label className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 cursor-pointer transition-all shadow-lg shadow-blue-200">
                                    <Upload size={20} />
                                    <span>Resim Yükle</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        disabled={uploading}
                                    />
                                </label>
                            </div>

                            {uploading && (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mx-auto mb-3"></div>
                                    <p className="text-blue-600">Resim yükleniyor...</p>
                                </div>
                            )}

                            {galleryLoading ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-300 border-t-blue-600 mx-auto mb-3"></div>
                                    <p className="text-gray-500">Yükleniyor...</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                    {galleryImages.map((image, index) => (
                                        <div key={index} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
                                            <img
                                                src={image.url}
                                                alt={`Gallery ${index + 1}`}
                                                className="w-full h-full object-cover transition-transform group-hover:scale-110"
                                                onError={(e) => {
                                                    e.target.src = '/logo.jpg';
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                                                <p className="text-white text-xs truncate mb-2">{image.filename}</p>
                                                <div className="flex space-x-2">
                                                    <a
                                                        href={image.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex-1 flex items-center justify-center p-2 bg-white/90 rounded-lg text-gray-900 text-xs font-medium hover:bg-white"
                                                    >
                                                        <ExternalLink size={14} className="mr-1" />
                                                        Aç
                                                    </a>
                                                    <button
                                                        onClick={() => handleDeleteImage(image.filename)}
                                                        className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {galleryImages.length === 0 && !galleryLoading && !uploading && (
                                <div className="text-center py-16 text-gray-500">
                                    <Image size={64} className="mx-auto mb-4 text-gray-300" />
                                    <p className="text-lg font-medium">Galeride hiç resim yok</p>
                                    <p className="text-sm mt-1">Yukarıdaki butondan yeni resim yükleyin</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>

            {/* Post Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                        <div className="p-6 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900">{editingPost ? 'Yazıyı Düzenle' : 'Yeni Yazı Ekle'}</h2>
                                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık *</label>
                                        <input
                                            type="text"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value, slug: generateSlug(e.target.value) })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            required
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">URL Slug *</label>
                                        <input
                                            type="text"
                                            value={formData.slug}
                                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            required
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Özet</label>
                                        <textarea
                                            value={formData.excerpt}
                                            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                                            rows={2}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                            placeholder="Yazı hakkında kısa açıklama..."
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Resim URL</label>
                                        <input
                                            type="url"
                                            value={formData.image}
                                            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            placeholder="https://... veya /gallery/resim.jpg"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Durum</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        >
                                            <option value="draft">Taslak</option>
                                            <option value="published">Yayınla</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">SEO - Meta Açıklama</label>
                                        <input
                                            type="text"
                                            value={formData.meta_description}
                                            onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            placeholder="SEO açıklaması..."
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">İçerik (HTML destekli) *</label>
                                        <RichTextEditor
                                            value={formData.content}
                                            onChange={(value) => setFormData({ ...formData, content: value })}
                                            placeholder="Yazı içeriğini buraya girin..."
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => { setShowModal(false); resetForm(); }}
                                        className="px-6 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                                    >
                                        İptal
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-200"
                                    >
                                        {editingPost ? 'Güncelle' : 'Kaydet'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
